import React, { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, SxProps, Tooltip, Typography, useTheme } from '@mui/material'
import isFunction from 'lodash/fp/isFunction'
import RemoveShoppingCartOutlinedIcon from '@mui/icons-material/RemoveShoppingCartOutlined'
import { WorkorderLineState } from '@cbgms/api'
import OrderButtonNewOrderline from './OrderButtonNewOrderLine'
import OrderButtonUpdateOrderLine from '@cbgms/components/src/order-parts/parts/buttons/OrderButtonUpdateOrderLine'
import { useOrderButtonContext } from '@cbgms/components/src/order-parts/context/buttons/OrderButtonContext'
import { useFeature } from '@cbgms/base/feature-flags'

export interface OrderButtonProps extends OrderButtonBaseProps {
  showDeleteOrder?: boolean
}

export interface OrderButtonBaseProps {
  hasGenartMatchValue?: number
  disabled?: boolean
  isLoading?: boolean
  size?: 'small' | 'medium'
  styling?: SxProps
  children?: (props: ChildProps) => JSX.Element
  unavailableSlot?: React.ReactElement
}

export interface DefaultOrderButtonsProps extends OrderButtonBaseProps {
  buttonText?: string
  showDeleteOrder?: boolean
}

interface ChildProps {
  updateProductRow: (exactQuantity: number) => Promise<void>
  addProductRow: (exactQuantity?: number) => Promise<void>
  deleteProductRow: (lineUUID: string) => Promise<void>
  orderLine: WorkorderLineState | undefined
  disabled: boolean
  isLoading: boolean

  size?: 'small' | 'medium'
  unitsPerSale?: number
  translationNames: 'order' | 'workorder'
}

export const DefaultOrderButtonsShortText: React.FC<OrderButtonBaseProps> = props => {
  const { t } = useTranslation()
  const buttonText = t('order.article-add-to-basket-short')
  return <DefaultOrderButtons {...props} buttonText={buttonText} showDeleteOrder />
}

const DefaultOrderButtons: React.FC<DefaultOrderButtonsProps> = ({
  hasGenartMatchValue,
  size,
  disabled,
  styling,
  buttonText,
  showDeleteOrder
}) => {
  const { t } = useTranslation()
  const {
    orderLine,
    isLoading,
    isButtonLocked,
    unitsPerSale,
    recommendedQuantity,
    forceNewLine,
    translationNames,
    onClickUpdateOrderLine,
    onClickDeleteOrderLine
  } = useOrderButtonContext()
  const hasMoreProductOnScreenDesign = useFeature('more-products-on-screen-design-v2')
  const hasAxnlReqqtyHaynespro = useFeature('axnl-reqqty-haynespro')
  const addQuantity = Math.max(recommendedQuantity || 0, unitsPerSale || 0, 1)
  let newAddQuantity = Math.ceil(addQuantity)
  if (hasGenartMatchValue !== undefined) {
    if (hasGenartMatchValue > addQuantity) {
      newAddQuantity = Math.ceil(hasGenartMatchValue / addQuantity) * addQuantity
    }
  } else {
    newAddQuantity = hasAxnlReqqtyHaynespro && addQuantity > 1 ? Math.ceil(addQuantity) : 0
  }
  const defaultText =
    translationNames === 'order'
      ? hasMoreProductOnScreenDesign
        ? `${t('order.article-add-to-cart')} ${newAddQuantity ? `(${newAddQuantity})` : ''}`
        : `${t('order.article-add-to-basket-short-add')} ${newAddQuantity ? `${newAddQuantity}` : ''} ${t('order.article-to-basket')}`
      : `${t('order.article-add-to-basket-short-add-workorder')} ${newAddQuantity ? `${newAddQuantity}` : ''} ${t(
          'order.article-to-workorder'
        )}`
  if (!orderLine || forceNewLine) {
    return (
      <OrderButtonNewOrderline
        hasGenartMatchValue={hasGenartMatchValue}
        size={size}
        styling={styling}
        disabled={disabled || isButtonLocked}
        buttonText={buttonText || defaultText}
      />
    )
  } else {
    return (
      <OrderButtonUpdateOrderLine
        size={size}
        styling={styling}
        orderLine={orderLine}
        updateProductRow={onClickUpdateOrderLine}
        deleteProductRow={onClickDeleteOrderLine}
        unitsPerSale={unitsPerSale}
        disabled={disabled || isButtonLocked}
        isLoading={isLoading}
        translationNames={translationNames}
        showDeleteOrder={showDeleteOrder}
      />
    )
  }
}

const DefaultUnavailableToolTip: React.FC<OrderButtonBaseProps> = ({ size }) => {
  const theme = useTheme()
  const { t } = useTranslation()
  return (
    <Tooltip data-testid='orderbutton-disabled-tooltip' title={<Typography variant='caption'>{t('order.article-unavailable')}</Typography>}>
      <Box data-testid='orderbutton-disabled-box'>
        <Button
          sx={{
            borderRadius: `${theme.shape.borderRadius}px`,
            padding: 0.7
          }}
          variant='outlined'
          data-testid='orderbutton-disabled'
          size={size}
          disabled
        >
          <RemoveShoppingCartOutlinedIcon />
        </Button>
      </Box>
    </Tooltip>
  )
}

// eslint-disable-next-line react/jsx-no-useless-fragment
export const EmptyUnavailable: React.FC = memo(() => <>{/* do not show when not availalbe */}</>)

export const OrderButton: React.FC<OrderButtonProps> = ({
  hasGenartMatchValue,
  disabled,
  size,
  styling,
  children,
  unavailableSlot,
  showDeleteOrder
}) => {
  const {
    productId,
    productIdType,
    isButtonLocked,
    isLoading,
    orderLine,
    onClickNewOrderLine,
    onClickUpdateOrderLine,
    onClickDeleteOrderLine,
    unitsPerSale,
    translationNames
  } = useOrderButtonContext()

  if (!productId || !productIdType) return unavailableSlot || <DefaultUnavailableToolTip />

  if (children && isFunction(children)) {
    return children({
      disabled: disabled || isButtonLocked,
      isLoading: !!isLoading,
      addProductRow: onClickNewOrderLine,
      updateProductRow: onClickUpdateOrderLine,
      deleteProductRow: onClickDeleteOrderLine,
      orderLine,
      size,
      unitsPerSale,
      translationNames
    })
  }

  return (
    <DefaultOrderButtons
      hasGenartMatchValue={hasGenartMatchValue}
      size={size}
      styling={styling}
      disabled={disabled}
      showDeleteOrder={showDeleteOrder}
    />
  )
}

export default OrderButton
